import React, { FC } from 'react'
import { Confirmation } from '../shared/Confirmation'
import { Dialog } from 'components/Dialog'
import { Heading } from 'components/Heading'
import { Text } from 'components/Text'
import { useTranslation } from 'react-i18next'
import { Flex } from 'components/Flex'
import { TrafficChart } from './TrafficChart'
import { ColumnChart } from 'components/ColumnChart'
import { Stack } from 'components/Stack'
import { useTrafficData } from 'services/traffic'
import { Spinner } from 'components/Spinner'
import { PointCoordinates } from 'models/map'
import { useMapEditorContext } from 'context/map-editor-context'
import styles from './TrafficToolWizard.module.scss'

export const TrafficToolWizard: FC = () => {
  const { t } = useTranslation()
  const [{ confirmed, editor }, mapEditorDispatch] = useMapEditorContext()

  const coordinates = editor.feature?.geometry?.coordinates as PointCoordinates

  const { data, status } = useTrafficData(confirmed ? coordinates : undefined)

  const onDialogClose = () => {
    mapEditorDispatch.cancelWizard()
  }

  if (confirmed) {
    return (
      <Dialog
        isOpen
        aria-label={t('traffic_report.title')}
        onClose={onDialogClose}
        className={styles.dialog}
      >
        <Stack space="medium">
          <Heading>{t('traffic_report.title')}</Heading>

          {status === 'loading' && (
            <Flex justify="center" align="center" className={styles.spinner}>
              <Spinner />
            </Flex>
          )}

          {status === 'success' && data && (
            <Stack space="medium">
              <Flex justify="between" align="center">
                <div className={styles.box}>
                  <Text weight="strong">{t('traffic_report.total_capacity')}</Text>
                  <Text>
                    {data.totalCapacity} {t('traffic_report.vehicles')}
                  </Text>
                </div>
                <div className={styles.box}>
                  <Text weight="strong">{t('traffic_report.average_speed')}</Text>
                  <Text>{data.averageSpeed.toFixed(2)} kmh</Text>
                </div>
                <div className={styles.box}>
                  <Text weight="strong">{t('traffic_report.peak_hour')}</Text>
                  <Text>{data.peakHour}</Text>
                </div>
              </Flex>

              <Heading size="widget">{t('traffic_report.capacity_profile')}</Heading>
              <div className={styles.chartContainer}>
                <ColumnChart
                  truncate={false}
                  data={{
                    values: [data.capacityProfile.map((item) => item.value)],
                    keys: data.capacityProfile.map((item) => item.label),
                    labels: data.capacityProfile.map((item) => item.label),
                    colors: [['#97b952']],
                  }}
                />
              </div>

              <Heading size="widget">{t('traffic_report.speed_and_demand')}</Heading>
              <div className={styles.chartContainer}>
                <TrafficChart
                  data={{
                    bars: data.speedDemandProfile.map((item) => item.speed),
                    line: data.speedDemandProfile.map((item) => item.demand),
                    labels: data.speedDemandProfile.map((item) => item.label),
                  }}
                />
              </div>
            </Stack>
          )}
        </Stack>
      </Dialog>
    )
  }

  return <Confirmation onConfirm={() => mapEditorDispatch.setConfirm(true)} />
}

TrafficToolWizard.displayName = 'TrafficToolWizard'
